import { useToast } from "@/components/toast-provider";
import { Routes } from "@/constants/routes";
import { useCategories } from "@/hooks/categories/useCategories";
import {
  deleteBusinessImage,
  getProfile,
  saveProfileStepOne,
  saveProfileStepTwo,
  submitProfile,
  uploadBusinessImage,
  type ProfileResponse,
} from "@/infrastructure/services/profileService";
import { defaultProfileValues } from "@/pages/perfil/(components)/stepper/constants";
import { mapProfile, normalizeCategory } from "@/pages/perfil/(components)/stepper/mappers";
import type { BusinessImage, ProfileFormData } from "@/pages/perfil/types";
import { useEmpreendedoraStore } from "@/stores/empreendedoraStore";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

function hasBusinessData(profile: ProfileResponse) {
  return [profile.titulo, profile.cnpj, profile.website, profile.categoria, profile.instituto, profile.instagram, profile.facebook, profile.tiktok, profile.descricao, profile.cep, profile.rua, profile.numero, profile.complemento, profile.bairro, profile.municipio, profile.preferencia, profile.imagensNegocio?.logo, profile.imagensNegocio?.banner].some((value) => typeof value === "string" ? value.trim().length > 0 : Boolean(value));
}

export function useProfileForm() {
  const form = useForm<ProfileFormData>({ defaultValues: defaultProfileValues, mode: "onChange" });
  const { categoryOptions: categories, loadCategories } = useCategories();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExistingBusiness, setHasExistingBusiness] = useState(false);
  const initialImages = useRef<{ cover: BusinessImage | null; logo: BusinessImage | null }>({ cover: null, logo: null });
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const setPerfilCompleto = useEmpreendedoraStore(
    (state) => state.setPerfilCompleto,
  );

  useEffect(() => {
    Promise.all([getProfile(), loadCategories()]).then(([profile, options]) => {
      const mapped = mapProfile(profile);
      setHasExistingBusiness(hasBusinessData(profile));
      form.reset({ ...defaultProfileValues, ...mapped, businessCategory: normalizeCategory(mapped.businessCategory ?? "", options) });
      initialImages.current = { cover: mapped.existingCoverImage || null, logo: mapped.existingLogoImage || null };
    }).catch((error) => showErrorToast(error instanceof Error ? error.message : "Não foi possível carregar o perfil.")).finally(() => setIsLoading(false));
  }, [form, loadCategories, showErrorToast]);

  const saveImages = useCallback(async (values: ProfileFormData) => {
    const slots = [
      { file: values.coverImage?.[0], alt: values.coverImageAlt, type: "BANNER" as const, current: values.existingCoverImage, initial: initialImages.current.cover },
      { file: values.logoImage?.[0], alt: values.logoImageAlt, type: "LOGO" as const, current: values.existingLogoImage, initial: initialImages.current.logo },
    ];
    for (const slot of slots) {
      if (slot.file) await uploadBusinessImage(slot.file, slot.alt, slot.type);
      if (slot.initial && !slot.current && !slot.file) await deleteBusinessImage(slot.initial.id);
    }
  }, []);

  const saveDraft = useCallback(async (step: number) => {
    setIsSaving(true);
    try { const values = form.getValues(); if (step === 0) await saveProfileStepOne(values); else { await saveProfileStepTwo(values); await saveImages(values); } showSuccessToast("Rascunho salvo com sucesso."); navigate(Routes.HOME); }
    catch (error) { showErrorToast(error instanceof Error ? error.message : "Não foi possível salvar o rascunho."); }
    finally { setIsSaving(false); }
  }, [form, navigate, saveImages, showErrorToast, showSuccessToast]);

  const publish = useCallback(async () => {
    setIsSaving(true);
    try { const values = form.getValues(); await saveProfileStepTwo(values); await saveImages(values); await submitProfile(values); setPerfilCompleto(true); if (hasExistingBusiness) { showSuccessToast("Perfil atualizado com sucesso."); navigate(Routes.HOME); } else navigate(Routes.PROFILE_FEEDBACK, { replace: true }); }
    catch (error) { showErrorToast(error instanceof Error ? error.message : "Não foi possível cadastrar o perfil."); }
    finally { setIsSaving(false); }
  }, [form, hasExistingBusiness, navigate, saveImages, setPerfilCompleto, showErrorToast, showSuccessToast]);

  return { categories, form, hasExistingBusiness, isLoading, isSaving, publish, saveDraft };
}
